import React, { useContext, useEffect, useState } from "react";
import { withTranslation } from "react-i18next";
import { Button, Form, Modal, Table } from "react-bootstrap";
import { BiPencil, BiPlus, BiTrash } from "react-icons/bi";
import { toast } from "react-toastify";
import { LoaderContext } from "../../../App";
import UOMService from "../../../core/service/UOMService";
import { LS_AUTHORITY_KEY } from "../../../core/constants/common";
import useHasPermission from "../../../core/hooks/useHasPermission";
import ConfirmationModal from "../../../components/ConfirmationModal/ConfirmationModal";
import UomVersionFormModal from "./UomVersionFormModal";
import { useNavigate } from "react-router-dom";

const UomVersionManagement = ({ t }) => {
	const [versions, setVersions] = useState([]);
	const [showDeleteModal, setShowDeleteModal] = useState(false);
	const [versionToDelete, setVersionToDelete] = useState(null);
	const [showVersionFormModal, setShowVersionFormModal] = useState(false);
	const [editingVersion, setEditingVersion] = useState(null);
	const [showCloneModal, setShowCloneModal] = useState(false);
	const [cloneVersions, setCloneVersions] = useState([]);
	const [selectedSourceVersion, setSelectedSourceVersion] = useState("");
	const { setLoaderDisplay } = useContext(LoaderContext);
	const lsData = JSON.parse(localStorage.getItem(LS_AUTHORITY_KEY) || "null");
	const { isSuperAdmin } = useHasPermission();
	const navigate = useNavigate();

	const getCompanyId = () => {
		if (isSuperAdmin && isSuperAdmin()) {
			return null;
		}
		return lsData?.profile?.company?.id || null;
	};
	const companyId = getCompanyId();

	const loadVersions = () => {
		setLoaderDisplay(true);
		UOMService.listVersions(companyId)
			.then((res) => {
				if (res.status === 200) {
					const list = Array.isArray(res.data) ? res.data : (res.data?.data || []);
					setVersions(list);
				}
			})
			.catch(() => {
				setVersions([]);
			})
			.finally(() => setLoaderDisplay(false));
	};

	useEffect(() => {
		loadVersions();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const handleAddVersion = () => {
		setEditingVersion(null);
		setShowVersionFormModal(true);
	};

	const handleEditVersion = (version) => {
		setEditingVersion(version);
		setShowVersionFormModal(true);
	};

	const handleDeleteVersion = (version) => {
		setVersionToDelete(version);
		setShowDeleteModal(true);
	};

	const handleDeleteConfirm = (confirmed) => {
		if (!confirmed || !versionToDelete) {
			setShowDeleteModal(false);
			setVersionToDelete(null);
			return;
		}
		setLoaderDisplay(true);
		UOMService.deleteVersion(versionToDelete.id, companyId)
			.then((res) => {
				if (res.status === 200 || res.status === 204) {
					toast.success(t("EmissionDatasetVersionManagement.deleteSuccess.label"));
					loadVersions();
				}
			})
			.catch(() => {})
			.finally(() => {
				setLoaderDisplay(false);
				setShowDeleteModal(false);
				setVersionToDelete(null);
			});
	};

	const loadVersionsForClone = () => {
		setLoaderDisplay(true);
		// Always fetch without companyId for cloning (super admin scope)
		UOMService.listVersions(null)
			.then((res) => {
				if (res.status === 200) {
					const list = Array.isArray(res.data) ? res.data : (res.data?.data || []);
					setCloneVersions(list);
				}
			})
			.catch(() => setCloneVersions([]))
			.finally(() => setLoaderDisplay(false));
	};

	const handleCloneVersion = () => {
		if (!selectedSourceVersion) {
			toast.error(t("EmissionDatasetVersionManagement.selectSourceVersion.label"));
			return;
		}
		setLoaderDisplay(true);
		UOMService.cloneVersion(parseInt(selectedSourceVersion, 10), companyId)
			.then((res) => {
				if (res.status === 200 || res.status === 201) {
					toast.success(t("EmissionDatasetVersionManagement.cloneSuccess.label"));
					setShowCloneModal(false);
					setSelectedSourceVersion("");
					loadVersions();
				}
			})
			.catch(() => {})
			.finally(() => setLoaderDisplay(false));
	};

	return (
		<div className="container" data-testid="uom-version-management">
			<div className="d-flex align-items-center justify-content-between mb-3">
				<h5 className="m-0">{t("UOM.label") || "UOM"}</h5>
				<div className="d-flex align-items-center gap-2">
					<Button
						variant="outline-secondary"
						onClick={() => {
							navigate(`../uom`);
						}}
						data-testid="back-button">
						{t("Back.label")}
					</Button>
					<Button variant="primary" onClick={handleAddVersion} data-testid="new-version-button">
						<BiPlus className="me-1" />
						{t("EmissionDatasetVersionManagement.newVersion.label")}
					</Button>
					<Button
						variant="outline-primary"
						onClick={() => {
							setSelectedSourceVersion("");
							loadVersionsForClone();
							setShowCloneModal(true);
						}}
						data-testid="clone-version-button"
					>
						{t("EmissionDatasetVersionManagement.cloneVersion.label")}
					</Button>
				</div>
			</div>

			<Table striped bordered hover responsive>
				<thead>
					<tr>
						<th>{t("EmissionDatasetVersionManagement.table.versionNumber.label")}</th>
						<th>{t("EmissionDatasetVersionManagement.table.versionName.label")}</th>
						<th>{t("EmissionDatasetVersionManagement.table.description.label")}</th>
						<th>{t("EmissionDatasetVersionManagement.table.createdBy.label")}</th>
						<th>{t("EmissionDatasetVersionManagement.table.createdDate.label")}</th>
						<th>{t("EmissionDatasetVersionManagement.table.actions.label")}</th>
					</tr>
				</thead>
				<tbody>
					{(versions || []).length > 0 ? (
						(versions || []).map((version) => (
							<tr key={version.id || version.versionNumber}>
								<td>{version.versionNumber}</td>
								<td>{version.versionName || "-"}</td>
								<td>{version.description || "-"}</td>
								<td>{version.createdBy || "-"}</td>
								<td>{version.createdDate ? new Date(version.createdDate).toLocaleString() : "-"}</td>
								<td>
									<div className="d-flex gap-2">
										<Button
											variant="outline-primary"
											size="sm"
											onClick={() => handleEditVersion(version)}
											data-testid={`edit-version-${version.id || version.versionNumber}`}
										>
											<BiPencil />
										</Button>
										{(version.versionNumber !== 1 || version.companyId > 0 )&& (
											<Button
												variant="danger"
												size="sm"
												onClick={() => handleDeleteVersion(version)}
												data-testid={`delete-version-${version.id || version.versionNumber}`}
											>
												<BiTrash />
											</Button>
										)}
									</div>
								</td>
							</tr>
						))
					) : (
						<tr>
							<td colSpan="6" className="text-center">
								{t("EmissionDatasetVersionManagement.noVersions.label")}
							</td>
						</tr>
					)}
				</tbody>
			</Table>

			{showDeleteModal && versionToDelete && (
				<ConfirmationModal
					modalShow={true}
					messageText={t("EmissionDatasetVersionManagement.deleteModal.message.label").replace(
						"{{versionName}}",
						versionToDelete.versionName || versionToDelete.versionNumber || ""
					)}
					callbackModal={handleDeleteConfirm}
					title={t("EmissionDatasetVersionManagement.deleteModal.title.label")}
					confirmBtn={t("Delete.label")}
					CancelBtn={t("Cancel.label")}
				/>
			)}

			{showVersionFormModal && (
				<UomVersionFormModal
					show={showVersionFormModal}
					onHide={() => {
						setShowVersionFormModal(false);
						setEditingVersion(null);
					}}
					version={editingVersion}
					onSuccess={() => {
						setShowVersionFormModal(false);
						setEditingVersion(null);
						loadVersions();
					}}
				/>
			)}

			<Modal
				show={showCloneModal}
				onHide={() => {
					setShowCloneModal(false);
					setSelectedSourceVersion("");
				}}
				centered
				data-testid="clone-version-modal"
			>
				<Modal.Header closeButton>
					<Modal.Title>{t("EmissionDatasetVersionManagement.cloneModal.title.label")}</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<Form.Group className="mb-3">
						<Form.Label>{t("EmissionDatasetVersionManagement.cloneModal.selectVersion.label")}</Form.Label>
						<Form.Select
							value={selectedSourceVersion}
							onChange={(e) => setSelectedSourceVersion(e.target.value)}
							data-testid="clone-version-select"
						>
							<option value="">
								{t("EmissionDatasetVersionManagement.cloneModal.selectVersion.placeholder.label")}
							</option>
							{(cloneVersions || []).map((version) => (
								<option key={version.versionNumber} value={version.versionNumber}>
									{version.versionName || `Version ${version.versionNumber}`}
								</option>
							))}
						</Form.Select>
					</Form.Group>
				</Modal.Body>
				<Modal.Footer>
					<Button
						variant="secondary"
						onClick={() => {
							setShowCloneModal(false);
							setSelectedSourceVersion("");
						}}
					>
						{t("Cancel.label")}
					</Button>
					<Button variant="primary" onClick={handleCloneVersion} disabled={!selectedSourceVersion}>
						{t("EmissionDatasetVersionManagement.cloneModal.confirm.label")}
					</Button>
				</Modal.Footer>
			</Modal>
		</div>
	);
};

export default withTranslation()(UomVersionManagement);

